import { InfoCircleOutlined } from '@ant-design/icons';
import { CommerceDeviceDoc, ExpoPermissionStatus, UserRole } from '@gooduncles/gu-app-schema';
import { Button, Popover, Spin, Tag } from 'antd';
import { FC, MouseEvent, useState } from 'react';

import { getStoreUserDevices } from 'src/utils/firebase/firebase-short-cut';

import styles from './StorePushStatus.module.scss';

import useSWR from 'swr';

interface StorePushStatusProps {
  storeId: string;
}

const permissionStatusKr: Record<ExpoPermissionStatus, string> = {
  granted: '허용',
  denied: '거부',
  undetermined: '미결정',
};

const permissionStatusColor: Record<ExpoPermissionStatus, string> = {
  granted: 'green',
  denied: 'red',
  undetermined: 'default',
};

const platformLabel: Record<'ios' | 'android', string> = {
  ios: 'iOS',
  android: 'Android',
};

const roleLabelKr: Record<UserRole, string> = {
  admin: '관리자',
  manager: '매니저',
  user: '사용자',
  unknown: '미지정',
};

/** FCM 발송 조건: 기기가 활성 상태이고 알림 권한이 허용된 경우 */
const canReceivePush = (device: CommerceDeviceDoc): boolean =>
  device.active && device.permissions.notification === 'granted';

/** 클릭이 채널 행으로 전파되어 채팅이 열리는 것을 막는다. */
const stopPropagation = (e: MouseEvent) => e.stopPropagation();

const StorePushStatus: FC<StorePushStatusProps> = ({ storeId }) => {
  const [open, setOpen] = useState(false);

  // 팝오버를 처음 열 때만 조회한다. (목록 전체를 미리 부르지 않기 위함)
  const { data, error, isLoading } = useSWR(
    open ? [storeId, 'store-user-devices'] : null,
    () => getStoreUserDevices(storeId),
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );

  const allDevices = data?.flatMap((entry) => entry.devices) ?? [];
  const summary = !data
    ? null
    : allDevices.some(canReceivePush)
      ? { color: 'green', text: '수신 가능' }
      : allDevices.length === 0
        ? { color: 'default', text: '앱 미설치' }
        : { color: 'red', text: '수신 불가' };

  const iconColor = summary?.color === 'green' ? '#52c41a' : summary?.color === 'red' ? '#ff4d4f' : undefined;

  const title = (
    <div className={styles.title}>
      <span>알림 수신 상태</span>
      {summary && <Tag color={summary.color}>{summary.text}</Tag>}
    </div>
  );

  const content = (
    <div className={styles.content} onClick={stopPropagation}>
      {isLoading && (
        <div className={styles.message}>
          <Spin size='small' /> 불러오는 중…
        </div>
      )}
      {error && <div className={styles.message}>기기 정보를 불러오지 못했습니다.</div>}
      {data && data.length === 0 && <div className={styles.message}>이 매장에 등록된 사용자가 없습니다.</div>}
      {data?.map(({ user, devices }) => (
        <div key={user._id} className={styles.userGroup}>
          <div className={styles.userHeader}>
            <span className={styles.userName}>{user.userName || user.email || user.userTel}</span>
            <Tag>{roleLabelKr[user.role]}</Tag>
          </div>
          {devices.length === 0 ? (
            <div className={styles.noDevice}>등록된 기기 없음</div>
          ) : (
            devices
              .slice()
              .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
              .map((device) => (
                <div key={device._id} className={styles.deviceRow}>
                  <span className={styles.deviceInfo}>
                    {platformLabel[device.platform]} {device.platformVersion}
                  </span>
                  <Tag color={device.active ? 'green' : 'default'}>{device.active ? '활성' : '비활성'}</Tag>
                  <Tag color={permissionStatusColor[device.permissions.notification]}>
                    알림 {permissionStatusKr[device.permissions.notification]}
                  </Tag>
                </div>
              ))
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen} trigger='click' title={title} content={content}>
      <Button
        type='text'
        size='small'
        icon={<InfoCircleOutlined />}
        onClick={stopPropagation}
        style={{ color: iconColor }}
        aria-label='알림 수신 상태 확인'
      />
    </Popover>
  );
};

export default StorePushStatus;
